import Header from 'common/js/header/header'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import DatePicker from 'react-datepicker';
import {
    GoogleMap,
    Marker,
    Polyline,
    useJsApiLoader,
} from "@react-google-maps/api";
import { getLiveVehicleHistorySvc } from '../js/vehicleSvc';

const lerp = (a, b, t) => a + (b - a) * t;
const getToday = () => new Date().toISOString().slice(0, 10);

// har bus ke liye alag color — palette se id ke hisaab se fixed color milta hai
const BUS_COLORS = [
  "#e53935", "#1565c0", "#2e7d32", "#f9a825", "#6a1b9a", "#00838f", "#d84315", "#c2185b",
  "#3949ab", "#00897b", "#7cb342", "#fb8c00", "#8e24aa", "#039be5", "#c0ca33", "#5d4037",
  "#d81b60", "#43a047", "#fdd835", "#1e88e5", "#f4511e", "#00acc1", "#9c27b0", "#546e7a",
];
const colorForId = (id) => {
  const s = String(id);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return BUS_COLORS[h % BUS_COLORS.length];
};
// bus shape ka SVG, diye gaye color ke saath (data-URI image)
const busSvg = (color) =>
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="${color}"><path d="M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4S4 2.5 4 6v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z"/></svg>`
  );
const DEFAULT_CENTER = { lat: 12.568338, lng: 78.591072 };
const REFRESH_INTERVAL_MS = 10000;
const TICK_MS = 100;        // har 100ms pe tick (smooth)
const PLAYBACK_BASE_SECONDS = 1200; // base   actual replay time = PLAYBACK_BASE_SECONDS / interval
//   interval 5  -> 600/5  = 120 sec (dheere)
//   interval 60 -> 600/60 = 10  sec (tez)
//   yani bada interval = tez replay. Ulta chahiye to neeche formula me '* interval' kar dena.
// speed purane bucket version jaisi: 1 real second = `interval` data-minutes
// (jaise pehle har bucket [= interval min] ~1 sec mein advance hota tha)
// to formaat  the input date here in the given piece of cod ehere
const formatDateForApi = (date) => {
    if (!date) return "";
    const [y, m, d] = date.split("-");
    return `${d}-${m}-${y}`;
};

// "1:05 PM" / "13:05" / "2026-06-15 13:05:00" / ISO / epoch -> minutes since midnight

// vehicals the time ko minites mai parse karene ka liye yaha par.
const parseClockToMins = (s) => {
    if (s == null || s === "") return null;
    const ampm = String(s).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (ampm) {
        let hh = Number(ampm[1]) % 12;
        if (/PM/i.test(ampm[3])) hh += 12;
        return hh * 60 + Number(ampm[2]);
    }
    const h24 = String(s).trim().match(/^(\d{1,2}):(\d{2})$/);
    if (h24) return Number(h24[1]) * 60 + Number(h24[2]);
    const d = new Date(isNaN(Number(s)) ? s : Number(s));
    if (!Number.isNaN(d.getTime())) return d.getHours() * 60 + d.getMinutes();
    return null;
};

// minutes -> "1:05 PM" (slider labels ke liye)
const minsToClock = (mins) => {
    const total = Math.max(0, Math.round(mins));
    let hh = Math.floor(total / 60) % 24;
    const mm = total % 60;
    const ampm = hh >= 12 ? "PM" : "AM";
    hh = hh % 12 || 12;
    return `${hh}:${String(mm).padStart(2, "0")} ${ampm}`;
};

// vehicle ki position given time pe (2 points ke beech ho to interpolate)


const getPositionAtTime = (points, mins) => {
    if (!points.length) return null;
    if (mins <= points[0].mins) return points[0];
    if (mins >= points[points.length - 1].mins) return points[points.length - 1];
    for (let i = 1; i < points.length; i++) {
        if (points[i].mins >= mins) {
            const p0 = points[i - 1], p1 = points[i];
            const span = p1.mins - p0.mins;
            const t = span === 0 ? 0 : (mins - p0.mins) / span;
            return { lat: lerp(p0.lat, p1.lat, t), lng: lerp(p0.lng, p1.lng, t) };
        }
    }
    return points[points.length - 1];
};

// us time tak ka travelled path (+ current point)
const getPathUpToTime = (points, mins, cur) => {
    const path = points.filter((p) => p.mins <= mins).map((p) => ({ lat: p.lat, lng: p.lng }));
    if (cur) path.push({ lat: cur.lat, lng: cur.lng });
    return path;
};

function LocationHistory() {
    const [loader, setLoader] = useState("none");
    const [trackingDt, setTrackingDt] = useState(new Date());
    const [interval, setIntervalValue] = useState("5");

    // ---- time-based state (no buckets) ----
    const [vehicleTracks, setVehicleTracks] = useState({}); // { id: { regsNo, points:[{lat,lng,mins}] } }
    const [durationInMins, setDurationInMins] = useState(0);
    const [currentMins, setCurrentMins] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);

    const mapRef = useRef(null);
    const isFetchingRef = useRef(false);
    const isFirstFetchRef = useRef(true);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: "AIzaSyC_G16AeOUmhNAP9FuTvreRhFTs31bL4mQ",
    });

    // current frame: har vehicle ka marker + path at currentMins
    const frame = useMemo(() => {
        const markers = {}, paths = {};
        Object.entries(vehicleTracks).forEach(([id, track]) => {
            const pos = getPositionAtTime(track.points, currentMins);
            if (!pos) return;
            markers[id] = { lat: pos.lat, lng: pos.lng, regsNo: track.regsNo };
            paths[id] = getPathUpToTime(track.points, currentMins, pos);
        });
        return { markers, paths };
    }, [vehicleTracks, currentMins]);

    function convert(str) {
        var date = new Date(str),
            mnth = ("0" + (date.getMonth() + 1)).slice(-2),
            day = ("0" + date.getDate()).slice(-2);
        // console.log([day, mnth, date.getFullYear()].join("-"));
        return [day, mnth, date.getFullYear()].join("-");
    }

    // ---- fetch + parse (flatten saare buckets -> per-vehicle time-ordered list) ----
    const getLocationHistory = useCallback(async (isRefresh = false) => {
        if (isFetchingRef.current) return;
        try {
            isFetchingRef.current = true;
            if (!isRefresh) setLoader("block");

            const data = await getLiveVehicleHistorySvc(convert(trackingDt), Number(interval));
            console.log("Location history backend response:", data);

            const mpIntervalBucket = data.mpIntervalBucket || {};
            const tracks = {};

            Object.values(mpIntervalBucket).forEach((bucketVehicles) => {
                Object.entries(bucketVehicles || {}).forEach(([id, vehicle]) => {
                    if (!tracks[id]) tracks[id] = { regsNo: vehicle?.regsNo || `Vehicle ${id}`, points: [] };
                    (vehicle?.points || []).forEach((pt) => {
                        const lat = Number(pt.lat);
                        const lng = Number(pt.lng);
                        const mins = parseClockToMins(pt.lastDttm);
                        if (Number.isFinite(lat) && Number.isFinite(lng) && mins != null) {
                            tracks[id].points.push({ lat, lng, mins });
                        }
                    });
                });
            });

            // time ke hisaab se sort
            Object.values(tracks).forEach((t) => t.points.sort((a, b) => a.mins - b.mins));

            console.log("Vehicle tracks:", tracks);

            setVehicleTracks(tracks);
            setDurationInMins(Number(data.durationInMins) || 0);

            if (!isRefresh) {
                setCurrentMins(0);
                setIsPlaying(true);
            }
        } catch (err) {
            console.log("Location history API error:", err);
        } finally {
            isFetchingRef.current = false;
            if (!isRefresh) setLoader("none");
        }
    }, [trackingDt, interval]);

    // first load + 10s silent refresh
    useEffect(() => {
        // setTrackingDt(new Date())
        if (isFirstFetchRef.current) {
            getLocationHistory();
            isFirstFetchRef.current = false;
        }
        const t = setInterval(() => getLocationHistory(true), REFRESH_INTERVAL_MS);
        return () => clearInterval(t);
    }, [getLocationHistory]);

    // playback: speed ab interval se RELATE karti hai (bada interval = tez replay)
    useEffect(() => {
        if (!isPlaying || !durationInMins) return;
        // replay time interval ke hisaab se: bada interval -> chhota playbackSeconds -> tez
        const playbackSeconds = PLAYBACK_BASE_SECONDS / (Number(interval) || 1);
        const minsPerTick = (durationInMins / (playbackSeconds * 1000)) * TICK_MS;
        const t = setInterval(() => {
            setCurrentMins((prev) => {
                const next = prev + minsPerTick;
                if (next >= durationInMins) { setIsPlaying(false); return durationInMins; }
                return next;
            });
        }, TICK_MS);
        return () => clearInterval(t);
    }, [isPlaying, durationInMins, interval]);

    // saare vehicles ko frame mein rakho   adjust tabhi jab koi viewport ke bahar nikle (no jank)
    useEffect(() => {
        if (!isLoaded || !mapRef.current) return;

        const positions = Object.values(frame.markers);
        if (!positions.length) return;

        const map = mapRef.current;
        const currentBounds = map.getBounds();

        const allVisible =
            currentBounds &&
            positions.every((p) =>
                currentBounds.contains(new window.google.maps.LatLng(p.lat, p.lng))
            );
        if (allVisible) return;

        const bounds = new window.google.maps.LatLngBounds();
        positions.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
        map.fitBounds(bounds, 64); // 64px padding har taraf
    }, [frame, isLoaded]);

    if (!isLoaded) return <h3>Loading Map...</h3>;

    return (
        <div className="MainContainer">
            <div
                style={{
                    position: "fixed",
                    zIndex: 10000,
                    width: 100,
                    height: 100,
                    marginLeft: -50,
                    marginTop: -50,
                    left: "50%",
                    top: "50%",
                    display: loader,
                }}
            >
                <img className="loderImg" src={require("common/images/loader.gif")} alt="Loader..." />
            </div>

            <Header />
            <ToastContainer autoClose={3000} hideProgressBar />

            {/* date + interval + search */}
            <div style={{ padding: 10, display: "flex", gap: 10, alignItems: "center" }}>
                {/* <input
          type="date"
          className="form-control"
          style={{ width: 180 }}
          value={trackingDt}
          onChange={(e) => setTrackingDt(e.target.value)}
        /> */}
                <DatePicker id="inp_toDate"
                    className='form-control text-center'
                    placeholderText="Date"
                    selected={trackingDt}
                    dateFormat="dd-MM-yyyy"
                    onChange={setTrackingDt}
                    autoComplete='off'
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    maxDate={new Date()}
                />
                <select
                    className="form-control"
                    style={{ width: 120 }}
                    value={interval}
                    onChange={(e) => setIntervalValue(e.target.value)}
                >
                    <option value="1">1 min</option>
                    <option value="5">5 min</option>
                    <option value="10">10 min</option>
                    <option value="20">20 min</option>
                    <option value="30">30 min</option>
                    <option value="60">60 min</option>
                </select>
                <button type="button" className="btn btn-info" onClick={() => getLocationHistory()}>
                    Search
                </button>
            </div>

            {/* timeline slider */}
            <div style={{ padding: "0 12px 10px", display: "flex", gap: 12, alignItems: "center" }}>
                <button type="button" className="btn btn-sm btn-light" onClick={() => setIsPlaying((p) => !p)}>
                 {isPlaying ? "⏸️" : "▶️"}
                </button>
                <span style={{ fontSize: 12, minWidth: 70 }}>{minsToClock(0)}</span>
                <input
                    type="range"
                    min={0}
                    max={durationInMins}
                    step={1}
                    value={currentMins}
                    onMouseDown={() => setIsPlaying(false)}                  // pakda -> ruk
                    onTouchStart={() => setIsPlaying(false)}
                    onChange={(e) => setCurrentMins(Number(e.target.value))} // scrub
                    onMouseUp={() => setIsPlaying(true)}                     // chhoda -> wahin se aage
                    onTouchEnd={() => setIsPlaying(true)}
                    style={{ flex: 1 }}
                />
                <span style={{ fontSize: 12, minWidth: 70, textAlign: "right" }}>{minsToClock(durationInMins)}</span>
                <span style={{ fontSize: 12, fontWeight: 600, minWidth: 80, textAlign: "right" }}>
                    {minsToClock(currentMins)}
                </span>
            </div>

            <div style={{ height: "calc(100vh - 165px)", width: "100%" }}>
                <GoogleMap
                    mapContainerStyle={{ height: "100%", width: "100%" }}
                    center={DEFAULT_CENTER}
                    zoom={14}
                    onLoad={(map) => {
                        mapRef.current = map;
                    }}
                >
                    {Object.entries(frame.paths).map(([id, points]) =>
                        points.length > 1 ? (
                            <Polyline
                                key={`path-${id}`}
                                path={points}
                                options={{
                                    strokeColor: colorForId(id),
                                    strokeOpacity: 0.8,
                                    strokeWeight: 4,
                                    geodesic: true,
                                }}
                            />
                        ) : null
                    )}

                    {Object.entries(frame.markers).map(([id, m]) => (
                        <Marker
                            key={`marker-${id}`}
                            position={{ lat: m.lat, lng: m.lng }}
                            title={m.regsNo}
                            icon={{
                                url: busSvg(colorForId(id)),
                                scaledSize: new window.google.maps.Size(32, 32),
                                anchor: new window.google.maps.Point(16, 16),
                            }}
                        />
                    ))}
                </GoogleMap>
            </div>
        </div>
    );
}

export default LocationHistory
// import Header from 'common/js/header/header';
// import { GoogleMap, Marker, useJsApiLoader , Polyline,} from "@react-google-maps/api";
// import { useEffect, useState } from 'react';
// import { getLiveVehicleHistorySvc } from '../js/vehicleSvc';


// // ---- helpers (component ke BAHAR) ----
// const getToday = () => new Date().toISOString().slice(0, 10);

// const formatDateForApi = (date) => {
//   if (!date) return "";
//   const [y, m, d] = date.split("-");
//   return `${d}-${m}-${y}`;
// };

// // "1:05 PM" -> minutes since midnight
// const parseClockToMins = (s) => {
//   if (!s) return null;
//   const m = String(s).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
//   if (!m) return null;
//   let hh = Number(m[1]) % 12;
//   if (/PM/i.test(m[3])) hh += 12;
//   return hh * 60 + Number(m[2]);
// };
// const minsToClock = (mins) => {
//   const total = Math.max(0, Math.round(mins));
//   let hh = Math.floor(total / 60) % 24;
//   const mm = total % 60;
//   const ampm = hh >= 12 ? "PM" : "AM";
//   hh = hh % 12 || 12;
//   return `${hh}:${String(mm).padStart(2, "0")} ${ampm}`;
// };

// const lerp = (a, b, t) => a + (b - a) * t;

// const DEFAULT_CENTER = { lat: 12.568338, lng: 78.591072 };

// function LocationHistory() {
//   const { isLoaded } = useJsApiLoader({
//     googleMapsApiKey: "AIzaSyC_G16AeOUmhNAP9FuTvreRhFTs31bL4mQ",
//   });

//   const [vehicleTracks, setVehicleTracks] = useState({}); // { id: { regsNo, points } }

//   const getLocationHistory = async () => {
//     const data = await getLiveVehicleHistorySvc(formatDateForApi(getToday()), 5);
//     console.log("Raw backend data", data);

//     const tracks = {};
//     Object.values(data.mpIntervalBucket || {}).forEach((bucketVehicles) => {
//       Object.entries(bucketVehicles || {}).forEach(([id, vehicle]) => {
//         if (!tracks[id]) tracks[id] = { regsNo: vehicle?.regsNo, points: [] };
//         (vehicle?.points || []).forEach((pt) => {
//           tracks[id].points.push({
//             lat: Number(pt.lat),
//             lng: Number(pt.lng),
//             mins: parseClockToMins(pt.lastDttm),
//           });
//         });
//       });
//     });

//     console.log("Parsed tracks", tracks);
//     setVehicleTracks(tracks);
//     setDurationInMins(Number(data.durationInMins) || 0);
//     setCurrentMins(0);
//   };


//   const getPositionAtTime = (points, mins) => {
//   if (!points.length) return null;
//   if (mins <= points[0].mins) return points[0];
//   if (mins >= points[points.length - 1].mins) return points[points.length - 1];
//   for (let i = 1; i < points.length; i++) {
//     if (points[i].mins >= mins) {
//       const p0 = points[i - 1], p1 = points[i];
//       const span = p1.mins - p0.mins;
//       const t = span === 0 ? 0 : (mins - p0.mins) / span;
//       return { lat: lerp(p0.lat, p1.lat, t), lng: lerp(p0.lng, p1.lng, t) };
//     }
//   }
//   return points[points.length - 1];
// };

// // us time tak bus jahan-jahan se guzri, unka list (+ abhi wali position)
// const getPathUpToTime = (points, mins, cur) => {
//   const path = points.filter((p) => p.mins <= mins).map((p) => ({ lat: p.lat, lng: p.lng }));
//   if (cur) path.push({ lat: cur.lat, lng: cur.lng });
//   return path;
// };
//   useEffect(() => {
//     getLocationHistory();
//   }, []);

//   if (!isLoaded) return <h3>Loading Map...</h3>;

//   return (
//     <div className="MainContainer">
//       <Header />
//       <div style={{ height: "80vh" }}>
//         <GoogleMap
//           mapContainerStyle={{ height: "100%", width: "100%" }}
//           center={DEFAULT_CENTER}
//           zoom={12}
//         >
//           {/* har bus ka PEHLA point ek marker se */}
//           {Object.entries(vehicleTracks).map(([id, track]) =>
//             track.points[0] ? (
//               <Marker
//                 key={id}
//                 position={{ lat: track.points[0].lat, lng: track.points[0].lng }}
//                 title={track.regsNo}
//               />
//             ) : null
//           )}

//              {Object.entries(frame.paths).map(([id, points]) =>
//                         points.length > 1 ? (
//                             <Polyline
//                                 key={`path-${id}`}
//                                 path={points}
//                                 options={{
//                                     strokeColor: colorForId(id),
//                                     strokeOpacity: 0.8,
//                                     strokeWeight: 4,
//                                     geodesic: true,
//                                 }}
//                             />
//                         ) : null
//                     )}

//         </GoogleMap>
        
        
//       </div>
//     </div>
//   );
// }

// export default LocationHistory;